/**
 * Google Ads language + geo target constants for the keyword research form.
 *
 * Reference: https://developers.google.com/google-ads/api/data/codes-formats
 *
 * Backend mirror: backend/google_ads_crew/keyword_research/geo_constants.py
 *
 * Curated list — top markets the platform actually targets. Adding new
 * ones is a one-line append to both files.
 */

export interface LanguageOption {
  /** Display label */
  label: string;
  /** Google's resource name (e.g., "languageConstants/1000") */
  value: string;
}

export interface CountryOption {
  /** Display label */
  label: string;
  /** Google's geo target resource name (e.g., "geoTargetConstants/2840") */
  value: string;
  /** ISO2 code — used for tenant locale matching */
  code: string;
}

export const LANGUAGES: LanguageOption[] = [
  { label: 'English', value: 'languageConstants/1000' },
  { label: 'Spanish', value: 'languageConstants/1003' },
  { label: 'French', value: 'languageConstants/1002' },
  { label: 'German', value: 'languageConstants/1001' },
  { label: 'Portuguese', value: 'languageConstants/1014' },
  { label: 'Italian', value: 'languageConstants/1004' },
  { label: 'Dutch', value: 'languageConstants/1010' },
  { label: 'Japanese', value: 'languageConstants/1005' },
];

export const COUNTRIES: CountryOption[] = [
  { label: 'United States', code: 'US', value: 'geoTargetConstants/2840' },
  { label: 'Canada', code: 'CA', value: 'geoTargetConstants/2124' },
  { label: 'United Kingdom', code: 'GB', value: 'geoTargetConstants/2826' },
  { label: 'Australia', code: 'AU', value: 'geoTargetConstants/2036' },
  { label: 'New Zealand', code: 'NZ', value: 'geoTargetConstants/2554' },
  { label: 'Ireland', code: 'IE', value: 'geoTargetConstants/2372' },
  { label: 'South Africa', code: 'ZA', value: 'geoTargetConstants/2710' },
  { label: 'Germany', code: 'DE', value: 'geoTargetConstants/2276' },
  { label: 'France', code: 'FR', value: 'geoTargetConstants/2250' },
  { label: 'Spain', code: 'ES', value: 'geoTargetConstants/2724' },
  { label: 'Italy', code: 'IT', value: 'geoTargetConstants/2380' },
  { label: 'Netherlands', code: 'NL', value: 'geoTargetConstants/2528' },
  { label: 'Sweden', code: 'SE', value: 'geoTargetConstants/2752' },
  { label: 'Norway', code: 'NO', value: 'geoTargetConstants/2578' },
  { label: 'Denmark', code: 'DK', value: 'geoTargetConstants/2208' },
  { label: 'Brazil', code: 'BR', value: 'geoTargetConstants/2076' },
  { label: 'Mexico', code: 'MX', value: 'geoTargetConstants/2484' },
  { label: 'Japan', code: 'JP', value: 'geoTargetConstants/2392' },
  { label: 'India', code: 'IN', value: 'geoTargetConstants/2356' },
  { label: 'Singapore', code: 'SG', value: 'geoTargetConstants/2702' },
  { label: 'Philippines', code: 'PH', value: 'geoTargetConstants/2608' },
  { label: 'Malaysia', code: 'MY', value: 'geoTargetConstants/2458' },
  { label: 'Indonesia', code: 'ID', value: 'geoTargetConstants/2360' },
  { label: 'Thailand', code: 'TH', value: 'geoTargetConstants/2764' },
  { label: 'United Arab Emirates', code: 'AE', value: 'geoTargetConstants/2784' },
];

/** Default selections when the tenant has no locale hint. */
export const DEFAULT_LANGUAGE = 'languageConstants/1000'; // English
export const DEFAULT_COUNTRY = 'geoTargetConstants/2840'; // US

/**
 * Pick a default country based on a free-text locale hint from the
 * tenant settings (e.g., "Australia", "AU", "Sydney, AU").
 *
 * Returns the geo constant value (e.g., "geoTargetConstants/2036") or
 * undefined if no confident match.
 */
export function resolveCountryFromHint(hint: string | null | undefined): string | undefined {
  if (!hint) return undefined;
  const normalized = hint.trim().toUpperCase();
  // Exact ISO2 match
  const byCode = COUNTRIES.find((c) => c.code === normalized);
  if (byCode) return byCode.value;
  // Exact name match (case-insensitive)
  const byName = COUNTRIES.find((c) => c.label.toUpperCase() === normalized);
  if (byName) return byName.value;
  // Substring — e.g., "Sydney, Australia" contains "AUSTRALIA"
  const bySubstring = COUNTRIES.find((c) =>
    normalized.includes(c.label.toUpperCase()),
  );
  if (bySubstring) return bySubstring.value;
  return undefined;
}
